<template>
  <div class="min-h-screen bg-gray-50">
    <section class="border-b border-gray-200 bg-white">
      <div class="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-10 lg:flex-row lg:items-center lg:justify-between">
        <div class="space-y-4">
          <div class="flex items-center gap-4">
            <img :src="logo" alt="Logotipo Asador San Miguel" class="h-12 w-auto rounded-full border border-gray-100 bg-white shadow-sm">
            <p class="text-xs uppercase tracking-[0.4em] text-emerald-700">Reservaciones Asador San Miguel</p>
          </div>
          <h1 class="text-3xl font-semibold text-gray-900 sm:text-4xl">Reserva tu mesa en línea</h1>
          <p class="text-base text-gray-600">
            Todas las reservaciones se procesan en este sistema y quedan sujetas a disponibilidad real.
            Solo aceptamos mesas de viernes a domingo y confirmamos por correo tan pronto revisamos tu solicitud.
          </p>
          <div
            v-if="paymentEnabled && effectivePaymentAmount > 0"
            class="inline-flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <span class="mt-1 h-2.5 w-2.5 rounded-full bg-amber-500"></span>
            <span>{{ paymentCallout }}</span>
          </div>
        </div>
        <div class="w-full rounded-3xl border border-gray-100 bg-gray-50 p-6 shadow-sm lg:w-auto lg:max-w-sm">
          <p class="text-sm font-medium text-gray-500">Servicio disponible</p>
          <p class="mt-1 text-2xl font-semibold text-gray-900">Viernes a Domingo</p>
          <ul class="mt-5 space-y-3 text-sm text-gray-600">
            <li class="flex items-start gap-2">
              <span class="mt-1 h-2 w-2 rounded-full bg-emerald-500"></span>
              La mesa se retiene 15 minutos desde la hora confirmada.
            </li>
            <li class="flex items-start gap-2">
              <span class="mt-1 h-2 w-2 rounded-full bg-emerald-500"></span>
              La experiencia dura máximo 2 horas y 30 minutos.
            </li>
            <li class="flex items-start gap-2">
              <span class="mt-1 h-2 w-2 rounded-full bg-emerald-500"></span>
              Niños cuentan como parte de la reservación.
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section class="mx-auto grid max-w-6xl gap-6 px-4 py-10 lg:grid-cols-[3fr_2fr]">
      <form
        class="space-y-10 rounded-3xl border border-gray-200 bg-white p-6 shadow-xl lg:p-8"
        @submit.prevent="makeReservation"
        novalidate
      >
        <div>
          <div class="flex items-center gap-4">
            <span class="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-600 text-lg font-semibold text-white">1</span>
            <div>
              <h2 class="text-lg font-semibold text-gray-900">Selecciona la fecha y número de personas</h2>
              <p class="text-sm text-gray-500">El sistema valida la disponibilidad y el límite de 100 personas por horario.</p>
            </div>
          </div>
          <div class="mt-6 grid gap-4 md:grid-cols-2">
            <div>
              <label for="reservation-date" class="mb-2 block text-sm font-medium text-gray-900">Fecha *</label>
              <input
                id="reservation-date"
                type="date"
                v-model="state.reservation.date"
                required
                class="block w-full rounded-2xl border bg-white py-2.5 px-3 text-sm text-gray-900 focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500"
                :class="v$.reservation.date.$error || timeError ? 'border-red-400 text-red-900 placeholder-red-700 focus:border-red-500 focus:ring-red-100' : 'border-gray-200'"
              >
              <p class="mt-2 text-xs text-gray-500">Selecciona viernes, sábado o domingo; el sistema valida la disponibilidad automáticamente.</p>
              <p class="mt-2 text-sm text-red-600" v-if="v$.reservation.date.$error">Selecciona una fecha disponible.</p>
            </div>
            <div>
              <label for="people" class="mb-2 block text-sm font-medium text-gray-900">Comensales *</label>
              <input
                id="people"
                type="number"
                min="1"
                :max="maxPeople"
                v-model.number="state.reservation.people"
                required
                :class="['block w-full rounded-2xl border text-sm focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500', v$.reservation.people.$error ? 'border-red-400 text-red-900 placeholder-red-700 focus:border-red-500' : 'border-gray-200 text-gray-900']"
              >
              <p class="mt-2 text-sm text-red-600" v-if="v$.reservation.people.$error">Indica cuántas personas asistirán.</p>
            </div>
          </div>
        </div>

        <div>
          <div class="flex items-center gap-4">
            <span class="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-600 text-lg font-semibold text-white">2</span>
            <div>
              <h2 class="text-lg font-semibold text-gray-900">Elige un horario disponible</h2>
              <p class="text-sm text-gray-500">Mostramos únicamente horarios con cupo en Firestore.</p>
            </div>
          </div>
          <div
            class="mt-6 flex items-start gap-3 rounded-2xl border px-4 py-3"
            :class="timeError ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-100 bg-emerald-50 text-emerald-800'"
          >
            <svg class="h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.75-11.5a.75.75 0 011.5 0v4.25a.75.75 0 01-1.5 0V6.5zM10 13a1 1 0 100 2 1 1 0 000-2z" clip-rule="evenodd"/>
            </svg>
            <span class="text-sm font-medium">{{ slotsMessage }}</span>
          </div>
          <div class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <button
              v-for="time in availableTimes"
              :key="time"
              type="button"
              class="rounded-2xl border px-4 py-3 text-sm font-semibold shadow-sm focus:outline-none focus:ring-4 focus:ring-emerald-200"
              :class="time === state.reservation.time ? 'border-emerald-600 bg-emerald-600 text-white shadow-emerald-100' : 'border-gray-200 bg-white text-gray-900 hover:border-emerald-300'"
              @click="selectTime(time)"
            >
              {{ formatTime(time) }}
            </button>
          </div>
          <input type="hidden" v-model="state.reservation.time">
          <p class="mt-2 text-sm text-red-600" v-if="timeError">{{ timeError }}</p>
          <p class="mt-2 text-sm text-red-600" v-else-if="v$.reservation.time.$error">Selecciona un horario.</p>
        </div>

        <div>
          <div class="flex items-center gap-4">
            <span class="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-600 text-lg font-semibold text-white">3</span>
            <div>
              <h2 class="text-lg font-semibold text-gray-900">Datos de contacto</h2>
              <p class="text-sm text-gray-500">Los utilizamos para enviarte confirmaciones y recordatorios.</p>
            </div>
          </div>
          <div class="mt-6 grid gap-4 md:grid-cols-2">
            <div>
              <label for="name" class="mb-2 block text-sm font-medium text-gray-900">Nombre completo *</label>
              <input
                id="name"
                type="text"
                v-model="state.reservation.name"
                required
                :class="['block w-full rounded-2xl border text-sm focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500', v$.reservation.name.$error ? 'border-red-400 text-red-900 placeholder-red-700 focus:border-red-500' : 'border-gray-200 text-gray-900']"
              >
              <p class="mt-2 text-sm text-red-600" v-if="v$.reservation.name.$error">Ingresa tu nombre completo.</p>
            </div>
            <div>
              <label for="phone" class="mb-2 block text-sm font-medium text-gray-900">Teléfono (10 dígitos) *</label>
              <input
                id="phone"
                type="tel"
                minlength="10"
                v-model="state.reservation.phone"
                required
                :class="['block w-full rounded-2xl border text-sm focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500', v$.reservation.phone.$error ? 'border-red-400 text-red-900 placeholder-red-700 focus:border-red-500' : 'border-gray-200 text-gray-900']"
              >
              <p class="mt-2 text-sm text-red-600" v-if="v$.reservation.phone.$error">Ingresa un número válido.</p>
            </div>
          </div>
          <div class="mt-4">
            <label for="email" class="mb-2 block text-sm font-medium text-gray-900">Correo electrónico *</label>
            <input
              id="email"
              type="email"
              v-model="state.reservation.email"
              required
              :class="['block w-full rounded-2xl border text-sm focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500', v$.reservation.email.$error ? 'border-red-400 text-red-900 placeholder-red-700 focus:border-red-500' : 'border-gray-200 text-gray-900']"
            >
            <p class="mt-2 text-sm text-red-600" v-if="v$.reservation.email.$error">Ingresa un correo válido.</p>
          </div>
          <div class="mt-4 grid gap-4 md:grid-cols-[2fr_1fr]">
            <div>
              <label for="specialNotes" class="mb-2 block text-sm font-medium text-gray-900">Notas especiales</label>
              <textarea
                id="specialNotes"
                rows="3"
                placeholder="Añade información relevante para el servicio."
                v-model="state.reservation.specialNotes"
                :disabled="noSpecialNotes"
                :class="['block w-full rounded-2xl border text-sm focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500', v$.reservation.specialNotes.$error && !noSpecialNotes ? 'border-red-400 text-red-900 placeholder-red-700 focus:border-red-500' : 'border-gray-200 text-gray-900']"
              ></textarea>
            </div>
            <div class="flex items-start">
              <label class="inline-flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  v-model="noSpecialNotes"
                  class="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                >
                No tengo notas especiales
              </label>
            </div>
          </div>
          <p class="mt-2 text-sm text-red-600" v-if="specialNotesError">{{ specialNotesError }}</p>
        </div>

        <div class="rounded-3xl bg-gray-50 p-5">
          <button
            type="submit"
            class="inline-flex min-h-[52px] w-full items-center justify-center rounded-full bg-emerald-600 px-5 py-3 text-center text-sm font-semibold leading-5 text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-700 focus:outline-none focus:ring-4 focus:ring-emerald-200 sm:w-auto sm:px-6 sm:text-base"
            :disabled="loading"
            :class="{ 'opacity-60 cursor-not-allowed': loading }"
          >
            <svg
              v-if="loading"
              aria-hidden="true"
              class="me-2 h-5 w-5 animate-spin fill-white text-emerald-200"
              viewBox="0 0 100 101"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span>{{ loading ? 'Procesando...' : submitLabel }}</span>
          </button>
        </div>
      </form>

      <aside class="space-y-4">
        <div class="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
          <h3 class="text-lg font-semibold text-gray-900">Resumen de la solicitud</h3>
          <dl class="mt-4 divide-y divide-gray-100 text-sm">
            <div
              v-for="item in summaryItems"
              :key="item.label"
              class="flex items-center justify-between py-3"
            >
              <dt class="text-gray-500">{{ item.label }}</dt>
              <dd class="font-semibold text-gray-900">{{ item.value }}</dd>
            </div>
          </dl>
        </div>

        <div class="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
          <h3 class="text-lg font-semibold text-gray-900">Reglas de servicio</h3>
          <ol class="mt-4 list-decimal space-y-3 pl-5 text-sm text-gray-600">
            <li v-for="rule in serviceRules" :key="rule.slice(0, 32)">{{ rule }}</li>
          </ol>
        </div>
      </aside>
    </section>
  </div>
</template>

<script>
import { ref, watch, onMounted, computed } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, email, minLength, minValue } from '@vuelidate/validators';
import { collection, getDocs, query, where, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/init';
import { parseLocalDate, formatLocalDate } from '../utils/date';
import { formatTimeLabel } from '../utils/time';
import { findPaymentOverrideForDate, resolveEffectivePaymentSettings } from '../utils/paymentOverrides';
import {
  createReservation as createReservationRequest
} from '../services/api/reservations';
import logoImage from '../assets/logo.png';

const SERVICE_RULES = [
  'Reservaciones deberán llegar 15 minutos antes. Puntualidad, mesa se retiene por 15 minutos. Podrá perder reservación y deberá esperar una mesa según disponibilidad. Se le atenderá.',
  'La persona que hace la reservación, es quién deberá presentarse en el Host con TODAS las personas a la hora que le corresponde. Si el grupo no está completo deberán esperar por todos para poder sentarse. Máximo de 3 tickets por reservación.',
  'Se prohíbe la entrada con alcohol. Todo vaso insulado deberá de estar vacío para entrar al restaurante (todas las veces que entre y salga). De lo contrario se le cobrará $5.00 por cuota de servicio. De traer algún vino de su predilección se le cobrará el descorche a $20.00.',
  'De traer bizcocho se cobrará un fee de $30.00 y deberán traer platos y cucharas (no damos vela).',
  'Toda RSVP realizada de 2 a 3 semanas de anticipación tendrá el privilegio de seleccionar si desean la mesa al aire libre (deberá notificarlo al reservar). Dependerá de disponibilidad, cantidad de personas, tiempo atmosférico y clima. No informamos de actividades y/o reservaciones que alquilan áreas en el restaurante.',
  'Cambios a la RSVP deberán hacerse con una semana de anticipación. Todo aumento o disminución de personas puede cambiar la selección de área o mesa. Reservaciones hechas tardías (mismo día a 7 días antes del día a reservar) los ubicaremos a disponibilidad de mesa.',
  'Favor contar niños, ellos también son parte de la RSVP.',
  'Tendrá un máximo de 2 horas y 1/2 en la mesa. Si se excede tendrán que pagar un cargo ($150 por hora o fracción de tiempo) y va a depender si la mesa está disponible para ofrecer tiempo adicional. Las 2 horas y 1/2 comienzan desde la hora de la RSVP.',
  'De haber atrasos por tiempo en restaurante las 2 horas y 1/2 comenzarán a partir de que se le asigne la mesa.',
  'Grupos de 25 personas en adelante se trabajan por cotizaciones (cenas, cumpleaños, bodas, baby gender, etc). Para información enviar mensaje de texto al 787-900-4682 con su nombre, día, hora, cantidad de personas, motivo de la actividad y un correo electrónico. Se le llamará para orientación y coordinación.',
  'Somos Petfriendly, por lo que deberá indicar si llevará a su mascota.',
  'Podrá encontrar nuestro menú en asadorsanmiguelpr.com.',
  'Toda persona que llegue sin RSVP podrá ser atendida de haber espacios disponibles. De lo contrario deberá anotarse en lista de espera hasta que surja un espacio. No se aceptarán walk-ins en temporadas altas y días que estemos a capacidad.',
  'Toda reclamación deberá hacerse antes de salir de las facilidades.',
  'Nos reservamos el derecho de admisión.'
];

const formatDateForSummary = (date) => {
  if (!date) {
    return 'Pendiente';
  }
  const parsedDate = parseLocalDate(date);
  if (!parsedDate) {
    return date;
  }
  return new Intl.DateTimeFormat('es-PR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  }).format(parsedDate);
};

const formatPeopleLabel = (value) => {
  const amount = Number(value) || 1;
  return `${amount} ${amount === 1 ? 'persona' : 'personas'}`;
};

const WEEKDAY_TIME_OPTIONS = [
  '12:00', '12:30', '13:00', '13:30', '14:45',
  '15:00', '15:30', '16:00', '16:30', '17:45',
  '18:00', '18:30', '19:00', '19:30'
];

const SUNDAY_TIME_OPTIONS = [
  '12:00', '12:30', '13:00', '13:30', '14:45',
  '15:00', '15:30', '16:00', '16:30', '17:45'
];

const WEEKEND_DAY_MAP = {
  5: 'friday',
  6: 'saturday',
  0: 'sunday'
};

const createDefaultWeeklyTemplates = () => ({
  friday: [...WEEKDAY_TIME_OPTIONS],
  saturday: [...WEEKDAY_TIME_OPTIONS],
  sunday: [...SUNDAY_TIME_OPTIONS],
  special: [...WEEKDAY_TIME_OPTIONS]
});

export default {
  name: 'ClientView',
  setup() {
    const state = ref({
      reservation: {
        name: '',
        email: '',
        phone: '',
        date: null,
        time: '',
        people: 2,
        specialNotes: ''
      }
    });

    const maxPeople = ref(50);
    const timeError = ref('');
    const specialNotesError = ref('');
    const noSpecialNotes = ref(false);
    const loading = ref(false);
    const availableTimes = ref([]);
    const disabledTimes = ref({});
    const weeklyTemplates = ref(createDefaultWeeklyTemplates());
    const paymentEnabled = ref(false);
    const paymentMode = ref('deposit');
    const paymentAmount = ref(0);
    const serviceFeeAmount = ref(0);
    const paymentCurrency = ref('USD');
    const paymentOverrides = ref([]);

    const rules = {
      reservation: {
        name: { required },
        email: { required, email },
        phone: { required, minLength: minLength(10) },
        date: { required },
        time: { required },
        people: { required, minValue: minValue(1) },
        specialNotes: {
          required: () =>
            noSpecialNotes.value ||
            state.value.reservation.specialNotes.trim() !== ''
        }
      }
    };

    const v$ = useVuelidate(rules, state);

    const getReservedTimes = async (date) => {
      const normalizedDate = normalizeDateKey(date);
      if (!normalizedDate) {
        return {};
      }
      const q = query(collection(db, 'reservations'), where('date', '==', normalizedDate));
      const querySnapshot = await getDocs(q);
      const reservedTimes = {};

      querySnapshot.forEach((reservationDoc) => {
        const reservation = reservationDoc.data();
        if (reservation.type === 'walkin' || reservation.source === 'admin_walkin') {
          return;
        }
        if (reservation.status === 'awaiting_payment' || (reservation.paymentRequired && reservation.paymentStatus === 'awaiting_payment')) {
          return;
        }
        if (!reservedTimes[reservation.time]) {
          reservedTimes[reservation.time] = 0;
        }
        reservedTimes[reservation.time] += Number(reservation.people) || 0;
      });

      return reservedTimes;
    };

    const normalizeDateKey = (date) => {
      if (!date) {
        return '';
      }
      if (date instanceof Date && !Number.isNaN(date.getTime())) {
        return formatLocalDate(date);
      }
      if (typeof date === 'string') {
        const parsed = parseLocalDate(date);
        return parsed ? formatLocalDate(parsed) : date;
      }
      return String(date);
    };

    const getDisabledTimesForDate = (date) => {
      const key = normalizeDateKey(date);
      const value = disabledTimes.value?.[key];
      if (Array.isArray(value)) {
        return value;
      }
      if (!value || typeof value !== 'object') {
        return [];
      }
      return Object.keys(value).filter((time) => value[time]);
    };

    const filterAvailableTimes = async () => {
      if (!state.value.reservation.date || !availableTimes.value.length) {
        return;
      }
      const reservedTimes = await getReservedTimes(state.value.reservation.date);
      availableTimes.value = availableTimes.value.filter((time) => {
        const totalPeopleForTime = reservedTimes[time] || 0;
        return totalPeopleForTime < 100;
      });
      if (!availableTimes.value.length) {
        timeError.value = 'Los horarios para esta fecha alcanzaron el cupo máximo.';
      }
      clearInvalidSelection();
    };

    const clearInvalidSelection = () => {
      if (!state.value.reservation.time) {
        return;
      }
      if (availableTimes.value.includes(state.value.reservation.time)) {
        return;
      }
      state.value.reservation.time = '';
      if (!timeError.value) {
        timeError.value = 'Ese horario ya no tiene cupo. Selecciona otro.';
      }
    };

    const checkRules = () => {
      timeError.value = '';
      availableTimes.value = [];

      const { date } = state.value.reservation;
      if (!date) {
        timeError.value = 'Selecciona una fecha para mostrar horarios.';
        return;
      }

      const selectedDate = parseLocalDate(date);
      const dayIndex = selectedDate ? selectedDate.getDay() : NaN;

      const specialDateRule = findPaymentOverrideForDate(paymentOverrides.value, date);

      if (![5, 6, 0].includes(dayIndex) && !specialDateRule) {
        timeError.value = 'Solo aceptamos reservaciones de viernes a domingo.';
        return;
      }

      const dayKey = WEEKEND_DAY_MAP[dayIndex];
      const baseTimes = dayKey
        ? weeklyTemplates.value[dayKey] || []
        : weeklyTemplates.value.special || weeklyTemplates.value.friday || WEEKDAY_TIME_OPTIONS;
      if (!baseTimes.length) {
        timeError.value = 'No hay horarios habilitados para este día.';
        return;
      }

      const disabled = getDisabledTimesForDate(date);
      availableTimes.value = baseTimes.filter((time) => !disabled.includes(time));

      if (!availableTimes.value.length) {
        timeError.value = 'No hay horarios habilitados para esta fecha.';
        clearInvalidSelection();
        return;
      }

      filterAvailableTimes();
    };

    onMounted(() => {
      const params = new URLSearchParams(window.location.search);
      const paymentResult = params.get('payment');
      if (paymentResult === 'success' || paymentResult === 'failure') {
        window.location.replace(`/payment-result${window.location.search}`);
        return;
      }

      const adminConfigRef = doc(db, 'adminConfig', 'reservations');
      onSnapshot(adminConfigRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          disabledTimes.value = data.disabledTimes || {};
          weeklyTemplates.value = data.weeklyTemplates || createDefaultWeeklyTemplates();
          paymentEnabled.value = Boolean(data.paymentEnabled);
          paymentMode.value = ['deposit', 'full'].includes(data.paymentMode) ? data.paymentMode : 'deposit';
          paymentAmount.value = Number(data.paymentAmount) || 0;
          serviceFeeAmount.value = Number(data.serviceFeeAmount) || 0;
          paymentCurrency.value = String(data.paymentCurrency || 'USD').trim().toUpperCase() || 'USD';
          paymentOverrides.value = Array.isArray(data.paymentOverrides) ? data.paymentOverrides : [];
          if (state.value.reservation.date) {
            checkRules();
          }
        }
      });
    });

    watch(() => state.value.reservation.date, () => {
      state.value.reservation.time = '';
      checkRules();
    });

    watch([disabledTimes, weeklyTemplates, paymentOverrides], () => {
      if (state.value.reservation.date) {
        checkRules();
      }
    }, { deep: true });

    watch(() => state.value.reservation.specialNotes, () => {
      if (state.value.reservation.specialNotes.trim().length) {
        specialNotesError.value = '';
      }
    });

    watch(noSpecialNotes, (checked) => {
      if (checked) {
        state.value.reservation.specialNotes = '';
        specialNotesError.value = '';
      }
    });

    const selectTime = (time) => {
      state.value.reservation.time = time;
      if (timeError.value && timeError.value.toLowerCase().includes('horario')) {
        timeError.value = '';
      }
    };

    const makeReservation = async () => {
      v$.value.$touch();

      if (v$.value.$invalid || timeError.value) {
        if (!noSpecialNotes.value && !state.value.reservation.specialNotes.trim()) {
          specialNotesError.value = 'Añade notas o marca la casilla si no tienes ninguna.';
        }
        return;
      }

      loading.value = true;
      try {
        const peopleCount = Number(state.value.reservation.people) || 0;
        const response = await createReservationRequest({
          ...state.value.reservation,
          people: peopleCount,
          source: 'client_web'
        });

        if (response.paymentRequired && response.paymentUrl) {
          window.location.assign(response.paymentUrl);
          return;
        }

        alert(response.message || `Solicitud pendiente, Verifique email para detalles,ID: ${response.reservationId}`);

        state.value.reservation = {
          name: '',
          email: '',
          phone: '',
          date: null,
          time: '',
          people: 2,
          specialNotes: ''
        };
        noSpecialNotes.value = false;
        specialNotesError.value = '';
        timeError.value = '';
        availableTimes.value = [];
        v$.value.$reset();
      } catch (error) {
        console.error('Error al crear la reservación:', error);
        alert(error.message || 'No se pudo procesar la solicitud de reservación.');
      } finally {
        loading.value = false;
      }
    };

    const formatTime = (time) => formatTimeLabel(time);

    const summaryItems = computed(() => [
      { label: 'Nombre', value: state.value.reservation.name || 'Por definir' },
      { label: 'Fecha', value: formatDateForSummary(state.value.reservation.date) },
      {
        label: 'Horario',
        value: state.value.reservation.time ? formatTime(state.value.reservation.time) : 'Selecciona un horario'
      },
      { label: 'Personas', value: formatPeopleLabel(state.value.reservation.people) }
    ]);

    const slotsMessage = computed(() => {
      if (!state.value.reservation.date) {
        return 'Selecciona una fecha para cargar horarios.';
      }
      if (timeError.value) {
        return timeError.value;
      }
      if (!availableTimes.value.length) {
        return 'Estamos validando el cupo en tiempo real.';
      }
      return `Hay ${availableTimes.value.length} horarios con cupo disponible.`;
    });

    const formatCurrency = (amount) => (
      new Intl.NumberFormat('es-PR', {
        style: 'currency',
        currency: paymentCurrency.value || 'USD'
      }).format((Number(amount) || 0) / 100)
    );

    const effectivePaymentSettings = computed(() => resolveEffectivePaymentSettings({
      paymentEnabled: paymentEnabled.value,
      paymentMode: paymentMode.value,
      paymentAmount: paymentAmount.value,
      paymentCurrency: paymentCurrency.value,
      paymentOverrides: paymentOverrides.value
    }, state.value.reservation.date));

    const effectivePaymentAmount = computed(() => Number(effectivePaymentSettings.value.paymentAmount) || 0);
    const appliedPaymentOverride = computed(() => effectivePaymentSettings.value.appliedPaymentOverride || null);

    const paymentCallout = computed(() => {
      if (!paymentEnabled.value || effectivePaymentAmount.value <= 0) {
        return '';
      }
      if (appliedPaymentOverride.value) {
        return `Depósito requerido para ${appliedPaymentOverride.value.label}: ${formatCurrency(effectivePaymentAmount.value)} por reservación. Se aplicará al ticket de la mesa.`;
      }
      return `Depósito requerido: ${formatCurrency(effectivePaymentAmount.value)} por reservación. Se aplicará al ticket de la mesa.`;
    });

    const submitLabel = computed(() => (
      paymentEnabled.value && effectivePaymentAmount.value > 0 ? 'Continuar con depósito' : 'Solicitar mesa'
    ));

    return {
      v$,
      state,
      maxPeople,
      makeReservation,
      checkRules,
      timeError,
      specialNotesError,
      noSpecialNotes,
      loading,
      availableTimes,
      paymentEnabled,
      paymentAmount,
      effectivePaymentAmount,
      serviceFeeAmount,
      paymentCallout,
      submitLabel,
      formatTime,
      summaryItems,
      slotsMessage,
      selectTime,
      logo: logoImage,
      serviceRules: SERVICE_RULES
    };
  }
};
</script>
